// Club Admin CRM Analytics - Feature Mapping Configuration
// Maps every club admin route to a feature name for usage tracking
// This tracks ACTUAL club admin behavior in the main golf app

export interface FeatureMapping {
  route: string;
  featureName: string;
  featureGroup: string;
  description: string;
}

/**
 * Complete mapping of Club Admin routes to analytics feature names
 * Used by trackPageView() to categorize club admin CRM usage
 */
export const CRM_FEATURE_MAPPING: FeatureMapping[] = [
  // Dashboard & Overview
  {
    route: '/club-admin/dashboard',
    featureName: 'admin_dashboard',
    featureGroup: 'overview',
    description: 'Main club admin dashboard with club overview'
  },

  // Member Management
  {
    route: '/club-admin/members',
    featureName: 'member_list',
    featureGroup: 'member_management',
    description: 'View and manage all club members'
  },
  {
    route: '/club-admin/members/:memberId',
    featureName: 'member_detail',
    featureGroup: 'member_management',
    description: 'View individual member details and profile'
  },
  {
    route: '/club-admin/member-profile/:memberId',
    featureName: 'member_profile_360',
    featureGroup: 'member_management',
    description: 'Comprehensive member 360 profile view'
  },

  // Event Management
  {
    route: '/club-admin/events',
    featureName: 'events_list',
    featureGroup: 'event_management',
    description: 'View and manage all club events'
  },
  {
    route: '/club-admin/events/:eventId',
    featureName: 'event_detail',
    featureGroup: 'event_management',
    description: 'View individual event details'
  },
  {
    route: '/club-admin/events/:eventId/edit',
    featureName: 'event_edit',
    featureGroup: 'event_management',
    description: 'Edit event settings and configuration'
  },
  {
    route: '/club-admin/events/:eventId/manage',
    featureName: 'event_management',
    featureGroup: 'event_management',
    description: 'Event management dashboard and tools'
  },
  {
    route: '/club-admin/events/:eventId/registrations',
    featureName: 'event_registrations',
    featureGroup: 'event_management',
    description: 'Manage event registrations and waitlists'
  },
  {
    route: '/club-admin/events/:eventId/communications',
    featureName: 'event_communications',
    featureGroup: 'event_management',
    description: 'Send communications to event participants'
  },

  // Tournament Management
  {
    route: '/club-admin/events/:eventId/tournament',
    featureName: 'tournament_view',
    featureGroup: 'tournament_management',
    description: 'Tournament overview and management'
  },
  {
    route: '/club-admin/events/:eventId/bracket',
    featureName: 'tournament_bracket',
    featureGroup: 'tournament_management',
    description: 'Tournament bracket view and management'
  },
  {
    route: '/club-admin/events/:eventId/live-tournament',
    featureName: 'live_tournament',
    featureGroup: 'tournament_management',
    description: 'Live tournament tracking and updates'
  },
  {
    route: '/club-admin/events/:eventId/group-phase',
    featureName: 'group_phase',
    featureGroup: 'tournament_management',
    description: 'Tournament group phase management'
  },
  {
    route: '/club-admin/events/:eventId/structure-manager',
    featureName: 'tournament_structure',
    featureGroup: 'tournament_management',
    description: 'Tournament structure configuration'
  },
  {
    route: '/club-admin/events/:eventId/admin-scores',
    featureName: 'score_management',
    featureGroup: 'tournament_management',
    description: 'Score entry and management'
  },
  {
    route: '/club-admin/events/:eventId/match-resolution',
    featureName: 'match_resolution',
    featureGroup: 'tournament_management',
    description: 'Resolve match disputes and issues'
  },
  {
    route: '/club-admin/tournament-dashboard',
    featureName: 'tournament_dashboard',
    featureGroup: 'tournament_management',
    description: 'Tournament management dashboard'
  },
  {
    route: '/club-admin/multi-round-showcase',
    featureName: 'multi_round_showcase',
    featureGroup: 'tournament_management',
    description: 'Multi-round tournament showcase'
  },

  // Communications
  {
    route: '/club-admin/communication',
    featureName: 'communication_management',
    featureGroup: 'communications',
    description: 'Manage club communications'
  },
  {
    route: '/club-admin/announcements',
    featureName: 'announcements',
    featureGroup: 'communications',
    description: 'Create and manage club announcements'
  },

  // Operations & Bookings
  {
    route: '/club-admin/bookings',
    featureName: 'booking_management',
    featureGroup: 'operations',
    description: 'Manage tee time bookings and reservations'
  },
  {
    route: '/club-admin/services/carts',
    featureName: 'cart_management',
    featureGroup: 'operations',
    description: 'Manage golf cart fleet and assignments'
  },
  {
    route: '/club-admin/courses',
    featureName: 'course_management',
    featureGroup: 'operations',
    description: 'Manage golf courses and hole configurations'
  },

  // Staff Management
  {
    route: '/club-admin/staff',
    featureName: 'staff_management',
    featureGroup: 'staff_operations',
    description: 'Manage club staff and schedules'
  },

  // Analytics & Reports
  {
    route: '/club-admin/analytics',
    featureName: 'club_analytics',
    featureGroup: 'analytics',
    description: 'View club performance analytics and reports'
  },

  // Configuration & Settings
  {
    route: '/club-admin/settings',
    featureName: 'club_settings',
    featureGroup: 'configuration',
    description: 'Configure club settings and preferences'
  }
];

/**
 * Get feature name from route path
 * Handles dynamic routes with parameters (e.g., :eventId, :memberId)
 */
export function getFeatureFromRoute(routePath: string): { featureName: string; featureGroup: string } | null {
  // Direct match first
  const directMatch = CRM_FEATURE_MAPPING.find(mapping => mapping.route === routePath);
  if (directMatch) {
    return {
      featureName: directMatch.featureName,
      featureGroup: directMatch.featureGroup
    };
  }

  // Pattern matching for dynamic routes
  for (const mapping of CRM_FEATURE_MAPPING) {
    const pattern = mapping.route.replace(/:[\w]+/g, '[^/]+');
    const regex = new RegExp(`^${pattern}$`);
    if (regex.test(routePath)) {
      return {
        featureName: mapping.featureName,
        featureGroup: mapping.featureGroup
      };
    }
  }

  return null;
}

/**
 * Get all features grouped by category
 */
export function getFeaturesByGroup(): Record<string, FeatureMapping[]> {
  return CRM_FEATURE_MAPPING.reduce((groups, mapping) => {
    if (!groups[mapping.featureGroup]) {
      groups[mapping.featureGroup] = [];
    }
    groups[mapping.featureGroup].push(mapping);
    return groups;
  }, {} as Record<string, FeatureMapping[]>);
}

/**
 * Get list of all unique feature names
 */
export function getAllFeatureNames(): string[] {
  return [...new Set(CRM_FEATURE_MAPPING.map(mapping => mapping.featureName))];
}

/**
 * Get list of all feature groups
 */
export function getAllFeatureGroups(): string[] {
  return [...new Set(CRM_FEATURE_MAPPING.map(mapping => mapping.featureGroup))];
}